import {
  BookingQuestion,
  DigitalProductProps,
  ProductCurrency,
} from "./digital-product.types";

/**
 * DigitalProduct Entity
 *
 * Domain entity representing a digital product with business rules.
 * Encapsulates data and behavior - callers should use methods, not pull data.
 */
export class DigitalProduct {
  private readonly props: DigitalProductProps;

  private constructor(props: DigitalProductProps) {
    this.props = props;
  }

  /**
   * Factory method to create a DigitalProduct entity with validation
   */
  static create(props: DigitalProductProps): DigitalProduct {
    if (props.id === undefined || props.id === null) {
      throw new Error("Digital product id is required");
    }
    if (!props.name || props.name.trim() === "") {
      throw new Error("Digital product name is required");
    }

    return new DigitalProduct(props);
  }

  // Getters

  get id(): number {
    return this.props.id;
  }

  get userId(): number {
    return this.props.userId;
  }

  get name(): string {
    return this.props.name;
  }

  get slug(): string {
    return this.props.slug;
  }

  get tinyUrl(): string {
    return this.props.tinyUrl;
  }

  get description(): string {
    return this.props.description;
  }

  get hideFromProfile(): boolean {
    return this.props.hideFromProfile;
  }

  get hideVariantFromProfile(): boolean {
    return this.props.hideVariantFromProfile;
  }

  get urlRedirect(): string | null {
    return this.props.urlRedirect;
  }

  get redirectType(): "none" | "telegram" | "url" | undefined {
    return this.props.redirectType;
  }

  get telegramGroupChatId(): string | null | undefined {
    return this.props.telegramGroupChatId;
  }

  get statusId(): number {
    return this.props.statusId;
  }

  get statusName(): string {
    return this.props.statusName;
  }

  get requestPhoneNumber(): boolean {
    return this.props.requestPhoneNumber;
  }

  get reference(): string {
    return this.props.reference;
  }

  /** How many units one transaction may buy. `null` when the coach set no cap. */
  get purchaseLimit(): number | null {
    return this.props.purchaseLimit ?? null;
  }

  get callPrice(): number {
    return this.props.callPrice;
  }

  get callPriceCurrency(): string {
    return this.props.callPriceCurrency;
  }

  get enableCurrencyConversion(): boolean {
    return this.props.enableCurrencyConversion;
  }

  /**
   * Whether the customer names their own price. The only Pricing Model the wire states
   * outright — Free and Single Fee stay derived from the amount.
   */
  get isPayWhatYouWant(): boolean {
    return this.props.isPayWhatYouWant;
  }

  get selectedCurrencies(): string[] | undefined {
    return this.props.selectedCurrencies;
  }

  get currencies(): ProductCurrency[] {
    if (this.props.currencies && this.props.currencies.length > 0) {
      return this.props.currencies;
    }
    return [
      {
        currency: this.props.callPriceCurrency,
        amount: this.props.callPrice,
        isBase: true,
      },
    ];
  }

  get bookingQuestions(): BookingQuestion[] {
    return this.props.bookingQuestions;
  }

  get coverImage(): string | undefined {
    return this.props.coverImage;
  }

  get createdAt(): string {
    return this.props.createdAt;
  }

  get updatedAt(): string {
    return this.props.updatedAt;
  }

  // Business Logic Methods

  /**
   * Check if this product is visible on the coach's public profile
   */
  isVisibleOnProfile(): boolean {
    return !this.props.hideFromProfile;
  }

  /**
   * Check if this product is free (zero price)
   */
  isFree(): boolean {
    return this.props.callPrice === 0;
  }

  /**
   * Check if this product has custom checkout questions
   */
  hasBookingQuestions(): boolean {
    return this.props.bookingQuestions.length > 0;
  }

  /**
   * Get the base currency (the primary pricing currency)
   */
  getBaseCurrency(): string {
    const baseCurrency = this.currencies.find((c) => c.isBase);
    return baseCurrency?.currency ?? this.props.callPriceCurrency;
  }

  /**
   * Check if the product has multiple currencies set
   */
  hasMultipleCurrencies(): boolean {
    return this.currencies.length > 1;
  }

  /**
   * Check if the product is active
   */
  isActive(): boolean {
    return this.props.statusId === 1;
  }

  /**
   * Convert entity back to plain props object
   */
  toProps(): DigitalProductProps {
    return {...this.props};
  }
}
